import React from "react";
import {
    Box, Checkbox, FormControlLabel, FormGroup, ListItemText,
    MenuItem, Select, SelectChangeEvent, Typography, useTheme
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { selectChartConfig, selectChartScalesAndLinesSettings } from "../../selectors/chart";
import { updateChartConfig } from "../../reducer/chart.reducer";
import { ScalesAndLinesSettings as ScalesAndLinesSettingsType } from "../chartV2/chart.typings";
import { chartSettingsColorRowLabelSx, chartSettingsColorRowSx, chartSettingsFormGroupSx, chartSettingsGridRowSx, chartSettingsRowControlsSx, chartSettingsSectionDividerSx, chartSettingsSectionTitleSx } from "./chartSettingsLayout";
import {
    defaultScalesAndLinesSettings,
    getDefaultHighLowLineColor,
} from "../../util/scalesAndLinesDefaults";
import { isTransformedChartType } from "../../util/chart";
import { ChartType } from "../../proto/generated/chart";
import ColorSelector from "../colorSelector/colorSelector";
import { useWidgetSettingsSelectMenuProps } from "../widgetDashboard/widgetSettingsMenuContext";

type Props = {
    viewKey: string;
};

const checkboxSx = {
    ml: 0,
    "& .MuiFormControlLabel-label": { fontSize: "14px" }
};

const HIGH_LOW_OPTIONS = [
    { value: "showHighLowLine", label: "Line" },
    { value: "showHighLowLabel", label: "Value" },
];

const PREV_DAY_HIGH_LOW_OPTIONS = [
    { value: "showPrevDayHighLowLine", label: "Line" },
    { value: "showPrevDayHighLowLabel", label: "Value" },
];

const CURRENT_DAY_HIGH_LOW_OPTIONS = [
    { value: "showCurrentDayHighLowLine", label: "Line" },
    { value: "showCurrentDayHighLowLabel", label: "Value" },
];

const INDICATOR_OPTIONS = [
    { value: "showIndicatorLabel", label: "Name" },
    { value: "showIndicatorValue", label: "Value" },
];

const SYMBOL_OPTIONS = [
    { value: "showSymbolLine", label: "Line" },
    { value: "showSymbolValue", label: "Value" },
];

const PREV_DAY_CLOSE_OPTIONS = [
    { value: "showPrevDayCloseLine", label: "Line" },
    { value: "showPrevDayCloseLabel", label: "Value" },
];

const TODAY_OPEN_OPTIONS = [
    { value: "showTodayOpenLine", label: "Line" },
    { value: "showTodayOpenLabel", label: "Value" },
];

const ScalesAndLinesSettings = ({ viewKey }: Props) => {
    const dispatch = useDispatch();
    const theme = useTheme();
    const selectMenuProps = useWidgetSettingsSelectMenuProps();
    const scalesAndLinesSettings = useSelector(selectChartScalesAndLinesSettings(viewKey));
    const chartType = useSelector(selectChartConfig(viewKey))?.chartType ?? ChartType.CANDLESTICK;
    const showCountdownSetting = !isTransformedChartType(chartType);

    const updateSettings = (partial: Partial<ScalesAndLinesSettingsType>) => {
        dispatch(updateChartConfig({
            viewKey,
            config: {
                scalesAndLinesSettings: {
                    ...scalesAndLinesSettings,
                    ...partial,
                }
            },
            createIfAbsent: false
        }));
    };

    const handleSelectClose = (event: React.SyntheticEvent) => {
        const target = event.currentTarget as HTMLElement;
        target?.blur?.();
        setTimeout(() => {
            target?.blur?.();
            const focusedElement = target?.querySelector(':focus') as HTMLElement;
            focusedElement?.blur?.();
            const selectInput = target?.querySelector('.MuiSelect-select') as HTMLElement;
            selectInput?.blur?.();
            const inputBase = target?.querySelector('.MuiInputBase-root') as HTMLElement;
            inputBase?.blur?.();
            const muiFocused = document.querySelector('.Mui-focused');
            if (muiFocused) {
                muiFocused.classList.remove('Mui-focused');
            }
            if (document.activeElement instanceof HTMLElement) {
                document.activeElement.blur();
            }
        }, 0);
    };
    const selectStyles = {
        flex: 1,
        minWidth: 0,
        fontSize: "13px",
        height: 32,
        backgroundColor: 'transparent',
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.87) !important' 
                : 'rgba(0, 0, 0, 0.87) !important',
        },
        '&:hover:not(.Mui-focused) .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.23) !important' 
                : 'rgba(0, 0, 0, 0.23) !important',
        },
        '&.Mui-focused': {
            backgroundColor: 'transparent',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'primary.main !important',
            borderWidth: '2px !important',
        },
        '&:not(.Mui-focused)': {
            backgroundColor: 'transparent',
        },
        '&:not(.Mui-focused) .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.23) !important' 
                : 'rgba(0, 0, 0, 0.23) !important',
            borderWidth: '1px !important',
        },
        '& .MuiSelect-select': {
            backgroundColor: 'transparent !important',
        },
        '& .MuiInputBase-input': {
            backgroundColor: 'transparent !important',
        },
    };

    const selectStylesFullWidth = {
        width: "100%",
        fontSize: "13px",
        height: 32,
        backgroundColor: 'transparent',
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.23) !important' 
                : 'rgba(0, 0, 0, 0.87) !important',
        },
        '&:hover:not(.Mui-focused) .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.23) !important' 
                : 'rgba(0, 0, 0, 0.23) !important',
        },
        '&.Mui-focused': {
            backgroundColor: 'transparent',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'primary.main !important',
            borderWidth: '2px !important',
        },
        '&:not(.Mui-focused)': {
            backgroundColor: 'transparent',
        },
        '&:not(.Mui-focused) .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.23) !important' 
                : 'rgba(0, 0, 0, 0.23) !important',
            borderWidth: '1px !important',
        },
        '& .MuiSelect-select': {
            backgroundColor: 'transparent !important',
        },
        '& .MuiInputBase-input': {
            backgroundColor: 'transparent !important',
        },
    };

    const highLowLineColor = scalesAndLinesSettings?.highLowLineColor ?? getDefaultHighLowLineColor(theme);

    const highLowLineColorOpacity = scalesAndLinesSettings?.highLowLineColorOpacity ?? defaultScalesAndLinesSettings.highLowLineColorOpacity;
    const prevDayHighLowLineColor = scalesAndLinesSettings?.prevDayHighLowLineColor ?? defaultScalesAndLinesSettings.prevDayHighLowLineColor;
    const prevDayHighLowLineColorOpacity = scalesAndLinesSettings?.prevDayHighLowLineColorOpacity ?? defaultScalesAndLinesSettings.prevDayHighLowLineColorOpacity;
    const currentDayHighLowLineColor = scalesAndLinesSettings?.currentDayHighLowLineColor ?? getDefaultHighLowLineColor(theme);
    const currentDayHighLowLineColorOpacity = scalesAndLinesSettings?.currentDayHighLowLineColorOpacity ?? defaultScalesAndLinesSettings.currentDayHighLowLineColorOpacity;
    const prevDayCloseLineColor = scalesAndLinesSettings?.prevDayCloseLineColor ?? defaultScalesAndLinesSettings.prevDayCloseLineColor;
    const prevDayCloseLineColorOpacity = scalesAndLinesSettings?.prevDayCloseLineColorOpacity ?? defaultScalesAndLinesSettings.prevDayCloseLineColorOpacity;
    const todayOpenLineColor = scalesAndLinesSettings?.todayOpenLineColor ?? defaultScalesAndLinesSettings.todayOpenLineColor;
    const todayOpenLineColorOpacity = scalesAndLinesSettings?.todayOpenLineColorOpacity ?? defaultScalesAndLinesSettings.todayOpenLineColorOpacity;

    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>, field: keyof ScalesAndLinesSettingsType) => {
        updateSettings({ [field]: event.target.checked });
    };

    const showHighLowLine = scalesAndLinesSettings?.showHighLowLine ?? false;
    const showHighLowLabel = scalesAndLinesSettings?.showHighLowLabel ?? false;

    const selectedValues = [
        ...(showHighLowLine ? ["showHighLowLine"] : []),
        ...(showHighLowLabel ? ["showHighLowLabel"] : []),
    ];

    const handleHighLowSelectChange = (event: SelectChangeEvent<string[]>) => {
        const values = event.target.value as string[];
        updateSettings({
            showHighLowLine: values.includes("showHighLowLine"),
            showHighLowLabel: values.includes("showHighLowLabel"),
        });
    };

    const showPrevDayHighLowLine = scalesAndLinesSettings?.showPrevDayHighLowLine ?? false;
    const showPrevDayHighLowLabel = scalesAndLinesSettings?.showPrevDayHighLowLabel ?? false;

    const selectedPrevDayHighLowOptions = [
        ...(showPrevDayHighLowLine ? ["showPrevDayHighLowLine"] : []),
        ...(showPrevDayHighLowLabel ? ["showPrevDayHighLowLabel"] : []),
    ];

    const handlePrevDayHighLowSelectChange = (event: SelectChangeEvent<string[]>) => {
        const values = event.target.value as string[];
        updateSettings({
            showPrevDayHighLowLine: values.includes("showPrevDayHighLowLine"),
            showPrevDayHighLowLabel: values.includes("showPrevDayHighLowLabel"),
        });
    };

    const showCurrentDayHighLowLine = scalesAndLinesSettings?.showCurrentDayHighLowLine ?? false;
    const showCurrentDayHighLowLabel = scalesAndLinesSettings?.showCurrentDayHighLowLabel ?? false;

    const selectedCurrentDayHighLowOptions = [
        ...(showCurrentDayHighLowLine ? ["showCurrentDayHighLowLine"] : []),
        ...(showCurrentDayHighLowLabel ? ["showCurrentDayHighLowLabel"] : []),
    ];

    const handleCurrentDayHighLowSelectChange = (event: SelectChangeEvent<string[]>) => {
        const values = event.target.value as string[];
        updateSettings({
            showCurrentDayHighLowLine: values.includes("showCurrentDayHighLowLine"),
            showCurrentDayHighLowLabel: values.includes("showCurrentDayHighLowLabel"),
        });
    };

    const showIndicatorValue = scalesAndLinesSettings?.showIndicatorValue ?? true;
    const showIndicatorLabel = scalesAndLinesSettings?.showIndicatorLabel ?? true;

    const selectedIndicatorOptions = [
        ...(showIndicatorLabel ? ["showIndicatorLabel"] : []),
        ...(showIndicatorValue ? ["showIndicatorValue"] : []),
    ];

    const handleIndicatorSelectChange = (event: SelectChangeEvent<string[]>) => {
        const values = event.target.value as string[];
        updateSettings({
            showIndicatorValue: values.includes("showIndicatorValue"),
            showIndicatorLabel: values.includes("showIndicatorLabel"),
        });
    };

    const showPrevDayCloseLine = scalesAndLinesSettings?.showPrevDayCloseLine ?? false;
    const showPrevDayCloseLabel = scalesAndLinesSettings?.showPrevDayCloseLabel ?? false;

    const selectedPrevDayCloseOptions = [
        ...(showPrevDayCloseLine ? ["showPrevDayCloseLine"] : []),
        ...(showPrevDayCloseLabel ? ["showPrevDayCloseLabel"] : []),
    ];

    const handlePrevDayCloseSelectChange = (event: SelectChangeEvent<string[]>) => {
        const values = event.target.value as string[];
        updateSettings({
            showPrevDayCloseLine: values.includes("showPrevDayCloseLine"),
            showPrevDayCloseLabel: values.includes("showPrevDayCloseLabel"),
        });
    };

    const showSymbolLine = scalesAndLinesSettings?.showSymbolLine ?? true;
    const showSymbolValue = scalesAndLinesSettings?.showSymbolValue ?? true;

    const selectedSymbolOptions = [
        ...(showSymbolLine ? ["showSymbolLine"] : []),
        ...(showSymbolValue ? ["showSymbolValue"] : []),
    ];

    const handleSymbolSelectChange = (event: SelectChangeEvent<string[]>) => {
        const values = event.target.value as string[];
        updateSettings({
            showSymbolLine: values.includes("showSymbolLine"),
            showSymbolValue: values.includes("showSymbolValue"),
        });
    };

    const showTodayOpenLine = scalesAndLinesSettings?.showTodayOpenLine ?? false;
    const showTodayOpenLabel = scalesAndLinesSettings?.showTodayOpenLabel ?? false;

    const selectedTodayOpenOptions = [
        ...(showTodayOpenLine ? ["showTodayOpenLine"] : []),
        ...(showTodayOpenLabel ? ["showTodayOpenLabel"] : []),
    ];

    const handleTodayOpenSelectChange = (event: SelectChangeEvent<string[]>) => {
        const values = event.target.value as string[];
        updateSettings({
            showTodayOpenLine: values.includes("showTodayOpenLine"),
            showTodayOpenLabel: values.includes("showTodayOpenLabel"),
        });
    };

    return (
        <Box>
            <Typography variant="subtitle1" sx={chartSettingsSectionTitleSx}>
                PRICE SCALE
            </Typography>
            <FormGroup sx={chartSettingsFormGroupSx}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={!!(scalesAndLinesSettings?.showPlusButton ?? true)}
                            onChange={(e) => handleCheckboxChange(e, "showPlusButton")}
                        />
                    }
                    label="Plus button"
                    sx={checkboxSx}
                />
                {showCountdownSetting ? (
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={!!(scalesAndLinesSettings?.showCountdown ?? true)}
                                onChange={(e) => handleCheckboxChange(e, "showCountdown")}
                            />
                        }
                        label="Countdown to bar close"
                        sx={checkboxSx}
                    />
                ) : null}
            </FormGroup>
            <Typography variant="subtitle1" sx={{ ...chartSettingsSectionTitleSx, ...chartSettingsSectionDividerSx }}>
                PRICE LABELS & LINES
            </Typography>
            <FormGroup sx={chartSettingsFormGroupSx}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={!!(scalesAndLinesSettings?.alignLabels ?? true)}
                            onChange={(e) => handleCheckboxChange(e, "alignLabels")}
                        />
                    }
                    label="No overlapping labels"
                    sx={checkboxSx}
                />
                <Box sx={chartSettingsGridRowSx}>
                    <Typography fontSize="14px" sx={chartSettingsColorRowLabelSx}>Visible range high and low</Typography>
                    <Box sx={chartSettingsRowControlsSx}>
                        <Select
                            multiple
                            size="small"
                            displayEmpty
                            value={selectedValues}
                            onChange={handleHighLowSelectChange}
                            onClose={handleSelectClose}
                            renderValue={(selected) => {
                                if (!(selected as string[]).length) return <Typography fontSize="13px" color="text.disabled">Hidden</Typography>;
                                return (
                                    <Typography fontSize="13px">
                                        {(selected as string[]).map(v => HIGH_LOW_OPTIONS.find(o => o.value === v)?.label).join(", ")}
                                    </Typography>
                                );
                            }}
                            sx={selectStyles}
                            MenuProps={{
                                ...selectMenuProps,
                                autoFocus: false,
                                disableAutoFocusItem: true,
                            }}
                        >
                            {HIGH_LOW_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value} dense>
                                    <Checkbox
                                        checked={selectedValues.includes(option.value)}
                                        size="small"
                                        sx={{ p: 0.5 }}
                                    />
                                    <ListItemText primary={option.label} primaryTypographyProps={{ fontSize: "13px" }} />
                                </MenuItem>
                            ))}
                        </Select>
                        <ColorSelector
                            compact
                            showOnlySwatch
                            color={highLowLineColor}
                            opacity={highLowLineColorOpacity}
                            onColorChange={(color, opacity) =>
                                updateSettings({ highLowLineColor: color, highLowLineColorOpacity: opacity })
                            }
                        />
                    </Box>
                </Box>
                <Box sx={chartSettingsGridRowSx}>
                    <Typography fontSize="14px" sx={chartSettingsColorRowLabelSx}>Prev. day high and low</Typography>
                    <Box sx={chartSettingsRowControlsSx}>
                        <Select
                            multiple
                            size="small"
                            displayEmpty
                            value={selectedPrevDayHighLowOptions}
                            onChange={handlePrevDayHighLowSelectChange}
                            onClose={handleSelectClose}
                            renderValue={(selected) => {
                                if (!(selected as string[]).length) return <Typography fontSize="13px" color="text.disabled">Hidden</Typography>;
                                return (
                                    <Typography fontSize="13px">
                                        {(selected as string[]).map(v => PREV_DAY_HIGH_LOW_OPTIONS.find(o => o.value === v)?.label).join(", ")}
                                    </Typography>
                                );
                            }}
                            sx={selectStyles}
                            MenuProps={{
                                ...selectMenuProps,
                                autoFocus: false,
                                disableAutoFocusItem: true,
                            }}
                        >
                            {PREV_DAY_HIGH_LOW_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value} dense>
                                    <Checkbox
                                        checked={selectedPrevDayHighLowOptions.includes(option.value)}
                                        size="small"
                                        sx={{ p: 0.5 }}
                                    />
                                    <ListItemText primary={option.label} primaryTypographyProps={{ fontSize: "13px" }} />
                                </MenuItem>
                            ))}
                        </Select>
                        <ColorSelector
                            compact
                            showOnlySwatch
                            color={prevDayHighLowLineColor}
                            opacity={prevDayHighLowLineColorOpacity}
                            onColorChange={(color, opacity) =>
                                updateSettings({ prevDayHighLowLineColor: color, prevDayHighLowLineColorOpacity: opacity })
                            }
                        />
                    </Box>
                </Box>
                <Box sx={chartSettingsGridRowSx}>
                    <Typography fontSize="14px" sx={chartSettingsColorRowLabelSx}>Current day high and low</Typography>
                    <Box sx={chartSettingsRowControlsSx}>
                        <Select
                            multiple
                            size="small"
                            displayEmpty
                            value={selectedCurrentDayHighLowOptions}
                            onChange={handleCurrentDayHighLowSelectChange}
                            onClose={handleSelectClose}
                            renderValue={(selected) => {
                                if (!(selected as string[]).length) return <Typography fontSize="13px" color="text.disabled">Hidden</Typography>;
                                return (
                                    <Typography fontSize="13px">
                                        {(selected as string[]).map(v => CURRENT_DAY_HIGH_LOW_OPTIONS.find(o => o.value === v)?.label).join(", ")}
                                    </Typography>
                                );
                            }}
                            sx={selectStyles}
                            MenuProps={{
                                ...selectMenuProps,
                                autoFocus: false,
                                disableAutoFocusItem: true,
                            }}
                        >
                            {CURRENT_DAY_HIGH_LOW_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value} dense>
                                    <Checkbox
                                        checked={selectedCurrentDayHighLowOptions.includes(option.value)}
                                        size="small"
                                        sx={{ p: 0.5 }}
                                    />
                                    <ListItemText primary={option.label} primaryTypographyProps={{ fontSize: "13px" }} />
                                </MenuItem>
                            ))}
                        </Select>
                        <ColorSelector
                            compact
                            showOnlySwatch
                            color={currentDayHighLowLineColor}
                            opacity={currentDayHighLowLineColorOpacity}
                            onColorChange={(color, opacity) =>
                                updateSettings({ currentDayHighLowLineColor: color, currentDayHighLowLineColorOpacity: opacity })
                            }
                        />
                    </Box>
                </Box>
                <Box sx={chartSettingsGridRowSx}>
                    <Typography fontSize="14px" sx={chartSettingsColorRowLabelSx}>Prev. day close</Typography>
                    <Box sx={chartSettingsRowControlsSx}>
                        <Select
                            multiple
                            size="small"
                            displayEmpty
                            value={selectedPrevDayCloseOptions}
                            onChange={handlePrevDayCloseSelectChange}
                            onClose={handleSelectClose}
                            renderValue={(selected) => {
                                if (!(selected as string[]).length) return <Typography fontSize="13px" color="text.disabled">Hidden</Typography>;
                                return (
                                    <Typography fontSize="13px">
                                        {(selected as string[]).map(v => PREV_DAY_CLOSE_OPTIONS.find(o => o.value === v)?.label).join(", ")}
                                    </Typography>
                                );
                            }}
                            sx={selectStyles}
                            MenuProps={{
                                ...selectMenuProps,
                                autoFocus: false,
                                disableAutoFocusItem: true,
                            }}
                        >
                            {PREV_DAY_CLOSE_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value} dense>
                                    <Checkbox
                                        checked={selectedPrevDayCloseOptions.includes(option.value)}
                                        size="small"
                                        sx={{ p: 0.5 }}
                                    />
                                    <ListItemText primary={option.label} primaryTypographyProps={{ fontSize: "13px" }} />
                                </MenuItem>
                            ))}
                        </Select>
                        <ColorSelector
                            compact
                            showOnlySwatch
                            color={prevDayCloseLineColor}
                            opacity={prevDayCloseLineColorOpacity}
                            onColorChange={(color, opacity) =>
                                updateSettings({ prevDayCloseLineColor: color, prevDayCloseLineColorOpacity: opacity })
                            }
                        />
                    </Box>
                </Box>
                <Box sx={chartSettingsGridRowSx}>
                    <Typography fontSize="14px" sx={chartSettingsColorRowLabelSx}>Today open</Typography>
                    <Box sx={chartSettingsRowControlsSx}>
                        <Select
                            multiple
                            size="small"
                            displayEmpty
                            value={selectedTodayOpenOptions}
                            onClose={handleSelectClose}
                            onChange={handleTodayOpenSelectChange}
                            renderValue={(selected) => {
                                if (!(selected as string[]).length) return <Typography fontSize="13px" color="text.disabled">Hidden</Typography>;
                                return (
                                    <Typography fontSize="13px">
                                        {(selected as string[]).map(v => TODAY_OPEN_OPTIONS.find(o => o.value === v)?.label).join(", ")}
                                    </Typography>
                                );
                            }}
                            sx={selectStyles}
                            MenuProps={{
                                ...selectMenuProps,
                                autoFocus: false,
                                disableAutoFocusItem: true,
                            }}
                        >
                            {TODAY_OPEN_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value} dense>
                                    <Checkbox
                                        checked={selectedTodayOpenOptions.includes(option.value)}
                                        size="small"
                                        sx={{ p: 0.5 }}
                                    />
                                    <ListItemText primary={option.label} primaryTypographyProps={{ fontSize: "13px" }} />
                                </MenuItem>
                            ))}
                        </Select>
                        <ColorSelector
                            compact
                            showOnlySwatch
                            color={todayOpenLineColor}
                            opacity={todayOpenLineColorOpacity}
                            onColorChange={(color, opacity) =>
                                updateSettings({ todayOpenLineColor: color, todayOpenLineColorOpacity: opacity })
                            }
                        />
                    </Box>
                </Box>
                <Box sx={chartSettingsGridRowSx}>
                    <Typography fontSize="14px">Symbol</Typography>
                    <Select
                        multiple
                        size="small"
                        displayEmpty
                        value={selectedSymbolOptions}
                        onChange={handleSymbolSelectChange}
                        onClose={handleSelectClose}
                        renderValue={(selected) => {
                            if (!(selected as string[]).length) return <Typography fontSize="13px" color="text.disabled">Hidden</Typography>;
                            return (
                                <Typography fontSize="13px">
                                    {(selected as string[]).map(v => SYMBOL_OPTIONS.find(o => o.value === v)?.label).join(", ")}
                                </Typography>
                            );
                        }}
                        sx={selectStylesFullWidth}
                        MenuProps={{
                            ...selectMenuProps,
                            autoFocus: false,
                            disableAutoFocusItem: true,
                        }}
                    >
                        {SYMBOL_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value} dense>
                                <Checkbox
                                    checked={selectedSymbolOptions.includes(option.value)}
                                    size="small"
                                    sx={{ p: 0.5 }}
                                />
                                <ListItemText primary={option.label} primaryTypographyProps={{ fontSize: "13px" }} />
                            </MenuItem>
                        ))}
                    </Select>
                </Box>
                <Box sx={chartSettingsGridRowSx}>
                    <Typography fontSize="14px">Indicator values</Typography>
                    <Select
                        multiple
                        size="small"
                        displayEmpty
                        value={selectedIndicatorOptions}
                        onChange={handleIndicatorSelectChange}
                         onClose={handleSelectClose}
                        renderValue={(selected) => {
                            if (!(selected as string[]).length) return <Typography fontSize="13px" color="text.disabled">Hidden</Typography>;
                            return (
                                <Typography fontSize="13px">
                                    {(selected as string[]).map(v => INDICATOR_OPTIONS.find(o => o.value === v)?.label).join(", ")}
                                </Typography>
                            );
                        }}
                        sx={selectStylesFullWidth}
                        MenuProps={{
                            ...selectMenuProps,
                            autoFocus: false,
                            disableAutoFocusItem: true,
                        }}
                    >
                        {INDICATOR_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value} dense>
                                <Checkbox
                                    checked={selectedIndicatorOptions.includes(option.value)}
                                    size="small"
                                    sx={{ p: 0.5 }}
                                />
                                <ListItemText primary={option.label} primaryTypographyProps={{ fontSize: "13px" }} />
                            </MenuItem>
                        ))}
                    </Select>
                </Box>
            </FormGroup>
        </Box>
    );
};

export default ScalesAndLinesSettings;
